/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {Edit} from '@mui/icons-material';
import {useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import {Grid, TableCell, TableBody, IconButton, Box} from '@mui/material';
import {FilterList as FilterListIcon} from '@mui/icons-material';
import {DateRange} from '@mui/lab/DateRangePicker';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  TRANSACTION_FILTER_TYPE_LIST,
  TRANSACTION_TYPES_CONSTANTS,
} from '../../constants/transaction';
import {ADD, BONUS} from '../../constants/operation';
import {ColorType} from '../../types/typeChip';
import {
  Breadcrumbs,
  Button,
  Chip,
  Table,
  Tooltips,
  ModalZoomImage,
} from '../../components';
import {path} from '../../routes/path';
import {typeRoutes} from '../../types/typeAllTransaction';
import {ColumnsType} from '../../types/typeTable';
import ModalFilter from './components/ModalFilter/ModalFilter';
import AllTransactionService from '../../services/AllTransaction/all-transaction.service';
import IslandPartnerService from '../../services/Islander/islander.service';
import {
  envRole,
  formatDate,
  getEndOfDate,
  getStartOfDate,
  hasPermission,
  formatLocalDateFromUTCTime,
  doesObjectHasPrototype,
} from '../../utils';
import {
  POINT_SUCCESS_COLOR,
  POINT_SUCCESS_LABEL,
  POINT_SUCCESS,
} from '../../constants/status';
import useAuth from '../../hooks/useAuth';
import {ROLE_SDC_ADMIN, USER_TYPE} from '../../constants/UserType';
import ExportExcelService from '../../services/ExportExcel/ExportExcel';
import queryString from 'query-string';
import success from '../../constants/success';

// Style
import {
  Divider,
  StyledTableRow,
  Typography,
  StyledStack,
  StyledAction,
  StyledIcon,
  StyledColorPoints,
  ReceiptImage,
} from './AllTransaction.style';
import {NavLink} from 'react-router-dom';

import {IsNullOrEmptyOrUndefined} from '../../utils/helpers';
import {ModalInformations as ModalInformationsProps} from '../../types/typeModals';
import ModalInformations from '../../components/Modal/ModalInformations/ModalInformations';
import {rbacPermissions} from '../../constants/roleBasedAccessControlConstants';
import PointMultiplierEventService from '../../services/PointMultiplierEvent/pointMultiplierEvent.service';
import {
  DEFAULT_FILTER_PAGE_SIZE,
  DISPLAY_MODE_CONSTANTS,
  DISPLAY_MODE_LABEL_FOR_PILLS,
  DISPLAY_MODE_LABELS,
  EXCESS_REDEMPTION_AMOUNT_OPTIONS,
  HIGHLIGHTED_GROUP_COLOR,
  POINT_STATUS_CONSTANTS,
  renderRedemptionAmount,
  renderTransactionType,
} from './AllTransactionUtil';

function AllTransaction() {
  const locationUrl = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const membershipId = locationUrl?.state?.membershipId;
  const merchantID = locationUrl?.state?.merchantId;
  const [isModalZoomImage, setIsModalZoomImage] = useState(false);
  const [imageZoom, setImageZoom] = useState('');
  const pointsStatusModalInfomations: ModalInformationsProps = {
    title: 'Points status',
    description: 'Based on date of transaction',
  };
  const format = 'DD/MM/YYYY';
  const formatDateTimes = 'DD/MM/YYYY HH:mm:ss';
  const {getUser, showNotification, checkRbac} = useAuth();
  const user = getUser() || {};
  const {merchantId, email} = user;
  const userType = +envRole;
  const isAdministrator = +envRole === USER_TYPE.ADMINISTRATOR;
  const allowTestItemModification = isAdministrator;
  const allowFilterPointMultiplierEventTitle = isAdministrator;

  const role = isAdministrator ? 'administrator' : 'merchant';
  const canUpdate = hasPermission(path.ISLAND_MEMBERS, 'update');
  // states
  const [order] = useState<'desc' | 'asc'>('asc');
  const [orderBy] = useState('');
  const [dense] = useState(false);
  const [isModalFilter, setIsModalFilter] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [dataSource, setDataSource] = useState<Array<any>>([]);
  const [totalPage, setTotalPage] = useState(0);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [optionByStatus, setOptionByStatus] = useState([]);
  const [nameContainsList, setNameContainsList] = useState([]);
  const transactionTypeFilterOptions = useMemo(() => {
    return Object.keys(TRANSACTION_FILTER_TYPE_LIST).reduce(
      (transactions: any, key: string) => {
        if (
          !isAdministrator &&
          Number(key) === TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER
        ) {
          return transactions;
        }
        transactions.push({
          title: TRANSACTION_FILTER_TYPE_LIST[Number(key)],
          value: key,
        });
        return transactions;
      },
      []
    );
  }, [isAdministrator]);

  const transactionExcessRedemptionAmountFilterOptions = Object.keys(
    EXCESS_REDEMPTION_AMOUNT_OPTIONS
  ).reduce((filterList: any, key: string) => {
    filterList.push({
      label: EXCESS_REDEMPTION_AMOUNT_OPTIONS[key],
      value: key,
    });
    return filterList;
  }, []);

  const [
    pointsMultiplierEventNameContainsList,
    setPointsMultiplierEventNameContainsList,
  ] = useState([]);
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    title: '',
    description: '',
  });

  const searchParamsObject = queryString.parse(searchParams.toString());

  const [valueFilter, setValueFilter] = useState({
    merchantId_eq: (merchantID || searchParams.get('merchantId_eq')) ?? '',
    processId_eq: searchParams.get('processId_eq') ?? '',
    associatedPointId_eq: searchParams.get('associatedPointId_eq') ?? '',
    salesCode_contains: searchParams.get('salesCode_contains') ?? '',
    createdDateTime_lte: searchParams.get('createdDateTime_lte') ?? '',
    createdDateTime_gte: searchParams.get('createdDateTime_gte') ?? '',
    pointMultiplierEventCreationId_eq:
      searchParams.get('pointMultiplierEventCreationId_eq') ?? '',
    accountId_eq: searchParams.get('accountId_eq') ?? '',
    id_eq: searchParams.get('id_eq') ?? '',
    pointMultiplierEventTitle_contains: allowFilterPointMultiplierEventTitle
      ? searchParams.get('pointMultiplierEventTitle_contains') ?? ''
      : '',
    transactionFilterType_by:
      searchParamsObject?.transactionFilterType_by !== undefined
        ? searchParamsObject?.transactionFilterType_by
            .split(',')
            .reduce((transactions, key) => {
              transactions.push({
                title: TRANSACTION_FILTER_TYPE_LIST[key],
                value: key,
              });
              return transactions;
            }, [])
        : undefined,
    pointStatusEnum_eq: searchParams.get('pointStatusEnum_eq') ?? '',
    operation_ne: searchParams.get('operation_ne') ?? '',
    isTest_eq: allowTestItemModification
      ? searchParams.get('isTest_eq') ?? ''
      : '',
    excessRedemptionAmount_filterBy:
      searchParams.get('excessRedemptionAmount_filterBy') ?? '',
  });
  const [valueRangePicker, setValueRangePicker] = useState<DateRange<Date>>([
    null,
    null,
  ]);

  const getDefaultParams = () => ({
    _page: 0,
    _size: DEFAULT_FILTER_PAGE_SIZE,
    ...(allowTestItemModification
      ? searchParamsObject.isTest_eq &&
        Object.values(DISPLAY_MODE_CONSTANTS).includes(
          searchParamsObject.isTest_eq as string
        )
        ? {
            isTest_eq: searchParamsObject.isTest_eq,
          }
        : {
            isTest_eq: DISPLAY_MODE_CONSTANTS.LIVE,
          }
      : {}),
    ...(allowFilterPointMultiplierEventTitle &&
    searchParamsObject.pointMultiplierEventTitle_contains
      ? {
          pointMultiplierEventTitle_contains:
            searchParamsObject.pointMultiplierEventTitle_contains,
        }
      : {}),
  });

  const [params, setParams] = useState({
    ...getDefaultParams(),
    ...{
      merchantId_eq: merchantID || undefined,
      operation_ne: searchParamsObject?.operation_ne || undefined,
      membershipId_eq: membershipId || undefined,
      pointMultiplierEventCreationId_eq:
        searchParamsObject?.pointMultiplierEventCreationId_eq || undefined,
      // pointMultiplierEventTitle_contains:
      //   searchParamsObject?.pointMultiplierEventTitle_contains || undefined,
      transactionFilterType_by:
        searchParamsObject?.transactionFilterType_by !== undefined
          ? searchParamsObject?.transactionFilterType_by
          : undefined,
      pointStatusEnum_eq: searchParamsObject?.pointStatusEnum_eq || undefined,
      excessRedemptionAmount_filterBy:
        searchParamsObject?.excessRedemptionAmount_filterBy || undefined,
    },
    ...{
      merchantId_eq: searchParamsObject.merchantId_eq ?? undefined,
      processId_eq: searchParamsObject.processId_eq ?? undefined,
      associatedPointId_eq:
        searchParamsObject.associatedPointId_eq ?? undefined,
      salesCode_contains: searchParamsObject.salesCode_contains ?? undefined,
      createdDateTime_lte: searchParamsObject.createdDateTime_lte ?? undefined,
      createdDateTime_gte: searchParamsObject.createdDateTime_gte ?? undefined,
      accountId_eq: searchParamsObject.accountId_eq ?? undefined,
      id_eq: searchParamsObject.id_eq ?? undefined,
    },
    ...(role === 'merchant' && merchantId
      ? {
          merchantId_eq: merchantId,
        }
      : {}),
    ...(role !== 'merchant' && merchantID
      ? {
          merchantId_eq: merchantID,
        }
      : {}),
  });

  const getDefaultParamsForApi = useCallback(
    () => ({
      isTest_eq: allowTestItemModification
        ? params.isTest_eq === 'none'
          ? undefined
          : params.isTest_eq
        : DISPLAY_MODE_CONSTANTS.LIVE,
      pointMultiplierEventTitle_contains: allowFilterPointMultiplierEventTitle
        ? params.pointMultiplierEventTitle_contains
        : undefined,
      // check excessRedemptionAmount_filterBy and get default params to filter for excessRedemptionAmount
      // Only allow 'eq_0' and 'gt_0'
      // Remove excessRedemptionAmount_filterBy from api params
      excessRedemptionAmount_filterBy: undefined,
      ...(['eq_0', 'gt_0'].includes(
        params.excessRedemptionAmount_filterBy as string
      )
        ? {
            [params.excessRedemptionAmount_filterBy === 'eq_0'
              ? 'excessAmount_eq'
              : 'excessAmount_gt']: 0,
          }
        : {}),
    }),
    [params, allowTestItemModification]
  );

  useEffect(() => {
    const defaultParams = getDefaultParams();
    const updatedParams: any = {};
    if (!doesObjectHasPrototype(searchParamsObject, '_page')) {
      updatedParams._page = defaultParams._page;
    }
    if (!doesObjectHasPrototype(searchParamsObject, '_size')) {
      updatedParams._size = defaultParams._size;
    }
    if (
      allowTestItemModification &&
      (!doesObjectHasPrototype(searchParamsObject, 'isTest_eq') ||
        !Object.values(DISPLAY_MODE_CONSTANTS).includes(
          searchParamsObject?.isTest_eq as string
        ))
    ) {
      updatedParams.isTest_eq = defaultParams.isTest_eq;
    } else if (
      !allowTestItemModification &&
      doesObjectHasPrototype(searchParamsObject, 'isTest_eq')
    ) {
      // IPP users should not have isTest_eq param
      updatedParams.isTest_eq = undefined;
    }
    if (
      !allowFilterPointMultiplierEventTitle &&
      doesObjectHasPrototype(
        searchParamsObject,
        'pointMultiplierEventTitle_contains'
      )
    ) {
      updatedParams.pointMultiplierEventTitle_contains = undefined;
    }
    if (Object.keys(updatedParams).length > 0) {
      setParams(prevState => ({
        ...prevState,
        ...updatedParams,
      }));
    }
    if (
      updatedParams.isTest_eq &&
      updatedParams.isTest_eq !== valueFilter.isTest_eq
    ) {
      setValueFilter({
        ...valueFilter,
        isTest_eq: updatedParams.isTest_eq,
      });
    }
  }, [searchParamsObject, valueFilter]);

  useEffect(() => {
    const updatedParams: any = {};
    const updatedValueFilter: any = {};

    // Check and get permitted transactionFilterType list
    if (
      doesObjectHasPrototype(searchParamsObject, 'transactionFilterType_by') &&
      typeof searchParamsObject.transactionFilterType_by === 'string' &&
      !!searchParamsObject.transactionFilterType_by
    ) {
      const searchParamsTransactionFilterTypeArray =
        searchParamsObject.transactionFilterType_by.split(',');
      const listOfPermittedTransactionType =
        transactionTypeFilterOptions.reduce((acc: any, item: any) => {
          acc.push(item.value);
          return acc;
        }, []);
      const filterSearchParamsTransactionType =
        searchParamsTransactionFilterTypeArray.filter(item =>
          listOfPermittedTransactionType.includes(item)
        );
      if (
        filterSearchParamsTransactionType.length !==
        searchParamsTransactionFilterTypeArray.length
      ) {
        updatedValueFilter.transactionFilterType_by =
          valueFilter.transactionFilterType_by?.filter((item: any) =>
            filterSearchParamsTransactionType.includes(item.value)
          );
        updatedParams.transactionFilterType_by =
          filterSearchParamsTransactionType.length > 0
            ? filterSearchParamsTransactionType.join(',')
            : undefined;
      }
    }
    // End check and get permitted transactionFilterType list

    // Check updatedParams and updatedValueFilter, then update states
    if (Object.keys(updatedParams).length > 0) {
      setParams(prevState => ({
        ...prevState,
        ...updatedParams,
      }));
    }
    if (Object.keys(updatedValueFilter).length > 0) {
      setValueFilter({
        ...valueFilter,
        ...updatedValueFilter,
      });
    }
  }, [searchParamsObject, transactionTypeFilterOptions, valueFilter]);

  const columns: Array<ColumnsType> = isAdministrator
    ? [
        {
          dataIndex: 'membershipId',
          numeric: false,
          disablePadding: false,
          label: 'Membership ID',
        },
        {
          dataIndex: 'transactionNo',
          numeric: false,
          disablePadding: false,
          label: 'Transaction no.',
        },
        {
          dataIndex: 'associatedPointId',
          numeric: false,
          disablePadding: false,
          label: 'Associated transaction no.',
        },
        {
          dataIndex: 'salesCode',
          numeric: false,
          disablePadding: false,
          label: 'Associated sales code',
        },
        {
          dataIndex: 'createdDate',
          numeric: false,
          disablePadding: false,
          label: 'Transaction date and time',
        },
        {
          dataIndex: 'transactional_type',
          numeric: false,
          disablePadding: false,
          label: 'Transaction type',
        },
        {
          dataIndex: 'amount',
          numeric: false,
          disablePadding: false,
          label: 'Amount spent',
        },
        {
          dataIndex: 'redemptionAmount',
          numeric: false,
          disablePadding: false,
          label: 'Redemption amount',
        },
        {
          dataIndex: 'excessAmount',
          numeric: false,
          disablePadding: false,
          label: 'Excess redemption amount',
        },
        {
          dataIndex: 'receiptImageUrl',
          numeric: false,
          disablePadding: false,
          label: 'Receipt image',
        },
        {
          dataIndex: 'receiptConfidence',
          numeric: false,
          disablePadding: false,
          label: 'System assurance level',
        },
        {
          dataIndex: 'limitedEventTitle',
          numeric: false,
          disablePadding: false,
          label: 'Points multiplier event name',
        },
        {
          dataIndex: 'points',
          numeric: false,
          disablePadding: false,
          label: 'Points',
        },
        {
          dataIndex: 'expiryDate',
          numeric: false,
          disablePadding: false,
          label: 'Points expiry date',
        },
        {
          dataIndex: 'status',
          numeric: false,
          disablePadding: false,
          label: 'Transaction status',
        },
        {
          dataIndex: 'pointsStatus',
          numeric: false,
          disablePadding: false,
          label: 'Points status',
          modalInfo: pointsStatusModalInfomations,
        },
        {
          dataIndex: null,
          numeric: false,
          disablePadding: false,
          label: '',
        },
      ]
    : [
        {
          dataIndex: 'membershipId',
          numeric: false,
          disablePadding: false,
          label: 'Membership ID',
        },
        {
          dataIndex: 'transactionNo',
          numeric: false,
          disablePadding: false,
          label: 'Transaction no.',
        },
        {
          dataIndex: 'associatedPointId',
          numeric: false,
          disablePadding: false,
          label: 'Associated transaction no.',
        },
        {
          dataIndex: 'salesCode',
          numeric: false,
          disablePadding: false,
          label: 'Associated sales code',
        },
        {
          dataIndex: 'createdDate',
          numeric: false,
          disablePadding: false,
          label: 'Transaction date and time',
        },
        {
          dataIndex: 'transactional_type',
          numeric: false,
          disablePadding: false,
          label: 'Transaction type',
        },
        {
          dataIndex: 'amount',
          numeric: false,
          disablePadding: false,
          label: 'Amount spent',
        },
        {
          dataIndex: 'redemptionAmount',
          numeric: false,
          disablePadding: false,
          label: 'Redemption amount',
        },
        {
          dataIndex: 'excessAmount',
          numeric: false,
          disablePadding: false,
          label: 'Excess redemption amount',
        },
        {
          dataIndex: 'receiptImageUrl',
          numeric: false,
          disablePadding: false,
          label: 'Receipt image',
        },
        {
          dataIndex: 'limitedEventTitle',
          numeric: false,
          disablePadding: false,
          label: 'Points multiplier event name',
        },
        {
          dataIndex: 'points',
          numeric: false,
          disablePadding: false,
          label: 'Points',
        },
        {
          dataIndex: 'status',
          numeric: false,
          disablePadding: false,
          label: 'Transaction status',
        },
        {
          dataIndex: null,
          numeric: false,
          disablePadding: false,
          label: '',
        },
      ];

  const onChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams(preState => ({
      ...preState,
      _page: newPage,
    }));
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setParams(preState => ({
      ...preState,
      _page: 0,
      _size: parseInt(event.target.value, 10),
    }));
  };

  const onClickIconButton = (idTransaction: string, row: any) => {
    const localTransactionId =
      isAdministrator &&
      row?.processId === POINT_SUCCESS.PENDING &&
      row?.workflowId
        ? row?.workflowId
        : idTransaction;
    if (membershipId) {
      return navigate(
        `/${path.ALL_TRANSACTION}/${localTransactionId}?membershipId=${membershipId}`,
        {
          state: {
            membershipId: membershipId,
            filterParams: {
              ...queryString.parse(searchParams.toString()),
            },
          },
        }
      );
    }
    if (merchantID) {
      return navigate(
        `/${path.ALL_TRANSACTION}/${localTransactionId}?merchantId=${merchantID}`,
        {
          state: {
            merchantId: merchantID,
            filterParams: {
              ...queryString.parse(searchParams.toString()),
            },
          },
        }
      );
    }
    return navigate(`/${path.ALL_TRANSACTION}/${localTransactionId}`, {
      state: {
        membershipId: null,
        merchantId: null,
        filterParams: {
          ...queryString.parse(searchParams.toString()),
        },
      },
    });
  };

  const renderAction = (value: string, row: any) => {
    if (
      !checkRbac({
        permissionString: [
          rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
          rbacPermissions.POINTS_TRANSACTIONS__LISTING__EDIT,
        ],
      })
    ) {
      return null;
    }
    if (!canUpdate) {
      if (focusId === value) {
        return (
          <StyledAction>
            <Tooltips title="View detail">
              <IconButton
                aria-label="details"
                size="medium"
                onClick={() => {
                  onClickIconButton(value, row);
                }}
              >
                <VisibilityIcon style={StyledIcon} />
              </IconButton>
            </Tooltips>
          </StyledAction>
        );
      }
    }
    if (focusId === value) {
      return (
        <StyledAction>
          <Tooltips title="Edit">
            <IconButton
              aria-label="details"
              onClick={() => {
                onClickIconButton(value, row);
              }}
              size="medium"
            >
              <Edit style={StyledIcon} />
            </IconButton>
          </Tooltips>
        </StyledAction>
      );
    }
  };

  const renderStatus = value => {
    if (!IsNullOrEmptyOrUndefined(value)) {
      const renderColor = POINT_SUCCESS_COLOR[value.processId];
      const renderLabel = POINT_SUCCESS_LABEL[value.processId];
      return <Chip label={renderLabel} color={renderColor as ColorType} />;
    }
  };

  const renderReceiptImage = image => {
    if (image === null) {
      return '--';
    }
    return (
      <ReceiptImage
        src={image}
        onClick={() => {
          toggleIsModalZoomImage();
          setImageZoom(image);
        }}
      />
    );
  };

  const renderSystemAssuranceLevel = (values: any) => {
    const percent =
      values?.receiptConfidence !== null
        ? values.receiptConfidence * 100
        : null;
    let color = '#6E6E6E';
    if (percent === null) {
      return (
        <Typography color={'GrayText'} variant="caption" fontWeight="bolder">
          --
        </Typography>
      );
    }

    if (percent < 80) {
      color = '#E66A25';
    } else if (percent >= 80) {
      color = '#2E7D32';
    }
    return (
      <Typography variant="h6" sx={{color: color}}>{`${percent}%`}</Typography>
    );
  };

  const deleteSearchParams = key => {
    if (searchParams.has(key)) {
      searchParams.delete(key);
      setSearchParams(searchParams);
    }
  };

  const handleDeleteChip = (type: string) => {
    if (allowTestItemModification && type === 'isTest_eq') {
      setValueFilter({
        ...valueFilter,
        isTest_eq: DISPLAY_MODE_CONSTANTS.ALL,
      });
      setParams(prevState => ({
        ...prevState,
        isTest_eq: DISPLAY_MODE_CONSTANTS.ALL,
      }));
    }
    if (type === 'date') {
      deleteSearchParams('createdDateTime_lte');
      deleteSearchParams('createdDateTime_gte');
      setValueRangePicker([null, null]);
      setValueFilter({
        ...valueFilter,
        createdDateTime_lte: '',
        createdDateTime_gte: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        createdDateTime_lte: undefined,
        createdDateTime_gte: undefined,
      }));
    }

    if (type === 'merchantId_eq') {
      deleteSearchParams('merchantId_eq');
      deleteSearchParams('merchantId_label');
      setValueFilter({
        ...valueFilter,
        merchantId_eq: '',
        operation_ne: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        operation_ne: undefined,
        merchantId_eq: undefined,
        ...(role === 'merchant' && merchantId
          ? {
              merchantId_eq: merchantId,
            }
          : {}),
      }));
    }

    if (type === 'pointMultiplierEventTitle_contains') {
      deleteSearchParams('pointMultiplierEventTitle_contains');
      setValueFilter({
        ...valueFilter,
        pointMultiplierEventTitle_contains: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        pointMultiplierEventTitle_contains: undefined,
      }));
    }

    if (type === 'pointMultiplierEventCreationId_eq') {
      deleteSearchParams('pointMultiplierEventCreationId_eq');
      deleteSearchParams('pointMultiplierEventCreationId_label');
      setValueFilter({
        ...valueFilter,
        pointMultiplierEventCreationId_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        pointMultiplierEventCreationId_eq: undefined,
      }));
    }
    if (type === 'processId_eq') {
      deleteSearchParams('processId_eq');
      deleteSearchParams('processId_label');
      setValueFilter({
        ...valueFilter,
        processId_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        processId_eq: undefined,
      }));
    }

    if (type === 'associatedPointId_eq') {
      deleteSearchParams('associatedPointId_eq');
      setValueFilter({
        ...valueFilter,
        associatedPointId_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        associatedPointId_eq: undefined,
      }));
    }

    if (type === 'salesCode_contains') {
      deleteSearchParams('salesCode_contains');
      setValueFilter({
        ...valueFilter,
        salesCode_contains: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        salesCode_contains: undefined,
      }));
    }

    if (type === 'accountId_eq') {
      deleteSearchParams('accountId_eq');
      setValueFilter({
        ...valueFilter,
        accountId_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        accountId_eq: undefined,
      }));
    }

    if (type === 'id_eq') {
      deleteSearchParams('id_eq');
      setValueFilter({
        ...valueFilter,
        id_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        id_eq: undefined,
      }));
    }

    if (type === 'pointStatusEnum_eq') {
      deleteSearchParams('pointStatusEnum_eq');
      setValueFilter({
        ...valueFilter,
        pointStatusEnum_eq: '',
      });
      setParams(preState => ({
        ...preState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        pointStatusEnum_eq: undefined,
      }));
    }

    if (type === 'excessRedemptionAmount_filterBy') {
      deleteSearchParams('excessRedemptionAmount_filterBy');
      setValueFilter({
        ...valueFilter,
        excessRedemptionAmount_filterBy: '',
      });
      setParams(prevState => ({
        ...prevState,
        _page: 0,
        _size: DEFAULT_FILTER_PAGE_SIZE,
        excessRedemptionAmount_filterBy: undefined,
      }));
    }
  };

  const toggleModal = () => {
    setIsModalFilter(!isModalFilter);
  };

  const toggleIsModalZoomImage = () => {
    setIsModalZoomImage(!isModalZoomImage);
  };

  const renderProcessLabelFilter = () => {
    const object = optionByStatus.find(
      item => item.value === valueFilter?.processId_eq
    );
    if (object === undefined || object === null) {
      return searchParams.get('processId_label') ?? undefined;
    }
    return object?.label;
  };

  const renderMerchantLabel = () => {
    if (
      nameContainsList.length === 0 &&
      searchParams.get('merchantId_label') !== null
    ) {
      return searchParams.get('merchantId_label');
    } else {
      return valueFilter?.merchantId_eq !== ''
        ? nameContainsList?.find(
            item => item.value === valueFilter?.merchantId_eq
          )?.label
        : undefined;
    }
  };

  const renderFieldPoints = (value: any) => {
    if (value) {
      let pointValue = value.pointValue;
      const transactionType = value.transactionType;
      if (
        (pointValue === null ||
          isNaN(pointValue) ||
          Number(pointValue) === 0) &&
        [
          TRANSACTION_TYPES_CONSTANTS.USE_VOUCHERS,
          TRANSACTION_TYPES_CONSTANTS.USED_OR_REMOVED_VOUCHERS,
        ].includes(transactionType)
      ) {
        return '--';
      }
      if (pointValue === null || isNaN(pointValue)) {
        pointValue = 0;
      }
      const operation = [
        TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER,
        TRANSACTION_TYPES_CONSTANTS.ISLANDER_POINTS_REDEMPTION,
      ].includes(transactionType)
        ? POINT_STATUS_CONSTANTS.NEGATIVE
        : POINT_STATUS_CONSTANTS.POSITIVE;
      return (
        <StyledColorPoints className={`points points--${operation}`}>
          {operation === POINT_STATUS_CONSTANTS.POSITIVE
            ? `+${pointValue}`
            : `-${pointValue}`}
        </StyledColorPoints>
      );
    }
  };

  const handleClean = () => {
    setValueRangePicker([null, null]);
    setValueFilter({
      accountId_eq: '',
      id_eq: '',
      processId_eq: '',
      associatedPointId_eq: '',
      salesCode_contains: '',
      transactionFilterType_by: undefined,
      merchantId_eq: '',
      createdDateTime_lte: '',
      createdDateTime_gte: '',
      pointMultiplierEventCreationId_eq: '',
      pointMultiplierEventTitle_contains: '',
      pointStatusEnum_eq: '',
      operation_ne: '',
      isTest_eq: valueFilter.isTest_eq,
      excessRedemptionAmount_filterBy: '',
    });
    setParams(preState => ({
      ...preState,
      _page: 0,
      _size: DEFAULT_FILTER_PAGE_SIZE,
      createdDateTime_lte: undefined,
      createdDateTime_gte: undefined,
      merchantId_eq: undefined,
      pointMultiplierEventCreationId_eq: undefined,
      pointMultiplierEventTitle_contains: undefined,
      accountId_eq: undefined,
      id_eq: undefined,
      processId_eq: undefined,
      associatedPointId_eq: undefined,
      salesCode_contains: undefined,
      transactionFilterType_by: undefined,
      pointStatusEnum_eq: undefined,
      operation_ne: undefined,
      excessRedemptionAmount_filterBy: undefined,
    }));
    deleteSearchParams('accountId_eq');
    deleteSearchParams('id_eq');
    deleteSearchParams('processId_eq');
    deleteSearchParams('associatedPointId_eq');
    deleteSearchParams('salesCode_contains');
    deleteSearchParams('transactionFilterType_by');
    deleteSearchParams('createdDateTime_lte');
    deleteSearchParams('createdDateTime_gte');
    deleteSearchParams('pointMultiplierEventCreationId_eq');
    deleteSearchParams('pointMultiplierEventTitle_contains');
    deleteSearchParams('pointStatusEnum_eq');
    deleteSearchParams('excessRedemptionAmount_filterBy');
  };

  const handleFilter = () => {
    if (valueFilter?.processId_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        processId_eq: valueFilter?.processId_eq,
      }));
    }

    if (valueFilter?.associatedPointId_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        associatedPointId_eq: valueFilter?.associatedPointId_eq,
      }));
    }

    if (valueFilter?.salesCode_contains) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        salesCode_contains: valueFilter?.salesCode_contains,
      }));
    }

    if (valueRangePicker[0] && valueRangePicker[1]) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        createdDateTime_lte: getEndOfDate(valueRangePicker[1], format),
        createdDateTime_gte: getStartOfDate(valueRangePicker[0], format),
      }));
    } else if (
      valueFilter?.createdDateTime_lte !== '' &&
      valueFilter?.createdDateTime_gte !== ''
    ) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        createdDateTime_lte: valueFilter?.createdDateTime_lte,
        createdDateTime_gte: valueFilter?.createdDateTime_gte,
      }));
    }

    if (valueFilter?.transactionFilterType_by) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        transactionFilterType_by: valueFilter?.transactionFilterType_by
          .map(item => item.value)
          .toString(),
      }));
    }

    if (valueFilter?.pointMultiplierEventTitle_contains) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        pointMultiplierEventTitle_contains:
          valueFilter?.pointMultiplierEventTitle_contains,
      }));
    }

    if (valueFilter?.merchantId_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        merchantId_eq: valueFilter?.merchantId_eq,
      }));
    }

    if (valueFilter?.pointMultiplierEventCreationId_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        pointMultiplierEventCreationId_eq:
          valueFilter?.pointMultiplierEventCreationId_eq,
      }));
    }
    if (valueFilter?.accountId_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        accountId_eq: valueFilter?.accountId_eq,
      }));
    }
    if (valueFilter?.id_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        id_eq: valueFilter?.id_eq,
      }));
    }
    if (valueFilter?.pointStatusEnum_eq) {
      setParams(preState => ({
        ...preState,
        _page: 0,
        pointStatusEnum_eq: valueFilter?.pointStatusEnum_eq,
      }));
    }
    if (valueFilter?.excessRedemptionAmount_filterBy) {
      setParams(prevState => ({
        ...prevState,
        _page: 0,
        excessRedemptionAmount_filterBy:
          valueFilter?.excessRedemptionAmount_filterBy,
      }));
    }
    if (allowTestItemModification && valueFilter?.isTest_eq) {
      setParams(prevState => ({
        ...prevState,
        _page: 0,
        isTest_eq: valueFilter?.isTest_eq,
      }));
    }

    toggleModal();
  };

  const toggleOpenModalInfomations = async (
    isOpen: boolean,
    title: string,
    desc: string
  ) => {
    setModalInfo({
      ...modalInfo,
      isOpen: isOpen,
      title: title,
      description: desc,
    });
  };

  const renderSpentAmount = (dataDetail: any) => {
    const matchedConditionGetVoucherType =
      dataDetail?.transactionType === TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER &&
      Number(dataDetail.spentAmount) !== 0;
    const matchedConditionOtherTransactionTypes =
      dataDetail?.transactionType !== TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER;
    if (
      dataDetail.spentAmount !== null &&
      !isNaN(dataDetail.spentAmount) &&
      (matchedConditionGetVoucherType || matchedConditionOtherTransactionTypes)
    ) {
      return parseFloat(dataDetail.spentAmount).toFixed(2);
    }
    return '--';
  };

  const renderExcessRedemptionAmount = (dataDetail: any) => {
    if (dataDetail?.excessAmount !== null) {
      return parseFloat(dataDetail.excessAmount).toFixed(2);
    }

    return '--';
  };

  const renderPointsStatus = (data: any) => {
    if (IsNullOrEmptyOrUndefined(data.expiryDateTime)) {
      return '--';
    }
    if (data.processId !== POINT_SUCCESS.APPROVED) {
      return '--';
    }
    let renderLabel = '';
    let renderColor = '';
    switch (data?.status) {
      case 1:
        renderLabel = 'Active';
        renderColor = 'success';
        break;
      default:
        renderLabel = 'Expired';
        renderColor = 'error';
        break;
    }
    return <Chip label={renderLabel} color={renderColor as ColorType} />;
  };

  const renderSimilarTransactionIds = dataDetail => {
    if (
      dataDetail?.duplicatedTransactionIds !== null &&
      Array.isArray(dataDetail.duplicatedTransactionIds) &&
      dataDetail.duplicatedTransactionIds.length > 0
    ) {
      return dataDetail.duplicatedTransactionIds.map((item, index) => (
        <div key={index + 1}>
          {index > 0 && <br />}
          <NavLink
            style={{textDecoration: 'none'}}
            to={`/all-transaction/${item}`}
          >
            {item}
          </NavLink>
        </div>
      ));
    }

    return '--';
  };

  const renderSimilarReceiptIds = dataDetail => {
    if (
      dataDetail?.duplicatedReceiptIds !== null &&
      Array.isArray(dataDetail.duplicatedReceiptIds) &&
      dataDetail.duplicatedReceiptIds.length > 0
    ) {
      return dataDetail.duplicatedReceiptIds.map((item, index) => (
        <div key={index + 1}>
          {index > 0 && <br />}
          <NavLink
            style={{textDecoration: 'none'}}
            to={`/all-transaction/receipt-validation-logs?id_eq=${item}`}
          >
            {item}
          </NavLink>
        </div>
      ));
    }

    return '--';
  };

  const getListAllTransaction = () => {
    setIsLoading(true);
    if (role) {
      new AllTransactionService()
        .getAll({
          ...params,
          ...getDefaultParamsForApi(),
          operation_ne:
            params.operation_ne !== null ? params.operation_ne : undefined,
          merchantId_eq:
            !isAdministrator && merchantId !== null && merchantId !== 0
              ? merchantId
              : params.merchantId_eq !== null
              ? params.merchantId_eq
              : undefined,
        })
        .then(res => {
          if (res?.data && Array.isArray(res.data.items)) {
            setDataSource(res.data.items);
            setTotalPage(res?.data?.total);
            setIsLoading(false);
          }
        })
        .catch(error => {
          setIsLoading(false);
          // Get api error => show notification or no items listing
          setError(error);
        });
    }
  };

  useEffect(() => {
    if (role === ROLE_SDC_ADMIN[USER_TYPE.ADMINISTRATOR]) {
      if (merchantID || membershipId) {
        // populate nameContainsList
        new IslandPartnerService()
          .getAll({
            id_eq: role === 'merchant' ? merchantId : undefined,
            status_eq: 1,
            _size: 2147483647,
          })
          .then(res => {
            if (res?.data?.items) {
              setNameContainsList(
                res?.data?.items?.reduce((items: any, item: any) => {
                  items.push({
                    label: item.name || '-',
                    value: item.id,
                  });
                  return items;
                }, [])
              );
            }
          });
      }

      new PointMultiplierEventService()
        .getAll({
          _page: 0,
          _size: 2147483647,
        })
        .then(res => {
          if (res?.data?.items) {
            setPointsMultiplierEventNameContainsList(
              res?.data?.items?.reduce((items: any, item: any) => {
                items.push({
                  label: item.title || '-',
                  value: item.title || '-',
                });
                return items;
              }, [])
            );
          }
        });
    }
  }, []);

  const getExport = () => {
    setIsLoadingExport(true);
    new ExportExcelService()
      .exportEmailExcelAllTransaction({
        ...params,
        ...getDefaultParamsForApi(),
        email: email,
        exportCSV_userType: userType,
      })
      .then(res => {
        setIsLoadingExport(false);
        if (res?.success) {
          showNotification({
            message: success.EXPORT_SUCCESS,
          });
        } else {
          showNotification({
            message: res?.errorMessage,
            variation: 'error',
          });
        }
      });
  };

  useEffect(() => {
    setSearchParams(
      queryString.stringify({
        ...params,
        operation_ne:
          params.operation_ne !== null ? params.operation_ne : undefined,
        processId_label:
          valueFilter?.processId_eq !== ''
            ? renderProcessLabelFilter()
            : undefined,
        merchantId_eq:
          !isAdministrator && merchantId !== null && merchantId !== 0
            ? merchantId
            : params.merchantId_eq !== null
            ? params.merchantId_eq
            : undefined,
        merchantId_label: renderMerchantLabel(),
      })
    );
    getListAllTransaction();
  }, [params, role, merchantId]);

  const getGroupId: any = (row: any) => row.associatedPointId || row.id;

  const onHoverRow = (row: any) => {
    setFocusId(row.id);
    const groupId = getGroupId(row);
    const group = document.querySelectorAll(
      `tr[data-group-id="${groupId}"]`
    ) as NodeListOf<HTMLElement>;
    group.forEach(
      item => (item.style.backgroundColor = HIGHLIGHTED_GROUP_COLOR)
    );
  };

  const onBlurRow = (row: any) => {
    setFocusId(null);
    const groupId = getGroupId(row);
    const group = document.querySelectorAll(
      `tr[data-group-id="${groupId}"]`
    ) as NodeListOf<HTMLElement>;
    group.forEach(item => (item.style.backgroundColor = ''));
  };

  const renderChipFilterTest = () => {
    const value = searchParams.get('isTest_eq');
    if (
      !allowTestItemModification ||
      typeof value !== 'string' ||
      !(value in DISPLAY_MODE_LABELS)
    ) {
      return null;
    }
    const label =
      DISPLAY_MODE_LABEL_FOR_PILLS[
        value as keyof typeof DISPLAY_MODE_LABEL_FOR_PILLS
      ] || DISPLAY_MODE_LABELS[value as keyof typeof DISPLAY_MODE_LABELS];
    if (label) {
      return (
        <Chip
          label={label}
          color={'default' as ColorType}
          onDelete={
            value !== DISPLAY_MODE_CONSTANTS.ALL
              ? () => {
                  handleDeleteChip('isTest_eq');
                }
              : undefined
          }
        />
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <Helmet title="All Transaction" />

      <ModalFilter
        visible={isModalFilter}
        onClose={toggleModal}
        handleFilter={handleFilter}
        handleClean={handleClean}
        valueFilter={valueFilter}
        valueRangePicker={valueRangePicker}
        setValueFilter={setValueFilter}
        setValueRangePicker={setValueRangePicker}
        setOptionByStatus={setOptionByStatus}
        optionByStatus={optionByStatus}
        nameContainsList={nameContainsList}
        setNameContainsList={setNameContainsList}
        pointsMultiplierEventNameContainsList={
          pointsMultiplierEventNameContainsList
        }
        transactionTypeFilterOptions={transactionTypeFilterOptions}
        transactionExcessRedemptionAmountFilterOptions={
          transactionExcessRedemptionAmountFilterOptions
        }
      />

      <ModalZoomImage
        visible={isModalZoomImage}
        image={imageZoom}
        alt="All Transaction Image Zoom"
        onClose={toggleIsModalZoomImage}
      />

      <ModalInformations
        onClose={() => toggleOpenModalInfomations(!modalInfo.isOpen, '', '')}
        visible={modalInfo.isOpen}
        title={modalInfo.title}
        description={modalInfo.description}
        iconWidth={80}
        iconHeight={80}
      />

      <Grid justifyContent="space-between" container spacing={2} columns={16}>
        <Grid item xs={8}>
          <Typography variant="h3" gutterBottom>
            All Transaction
          </Typography>
          <Breadcrumbs routes={[typeRoutes('All Transaction', null, true)]} />
        </Grid>
        <Grid
          item
          xs={8}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {checkRbac(
            {
              permissionString:
                rbacPermissions.POINTS_TRANSACTIONS__LISTING__EXPORT,
            },
            <div
              style={{
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                onClick={getExport}
                width="100px"
                variant="outlined"
                loading={isLoadingExport}
              >
                Export
              </Button>
            </div>
          )}
          <StyledStack direction="row" spacing={3}>
            {searchParams.get('createdDateTime_lte') !== null &&
              searchParams.get('createdDateTime_gte') !== null && (
                <Chip
                  label={`${formatDate(
                    searchParams.get('createdDateTime_gte'),
                    format
                  )} -
                    ${formatDate(
                      searchParams.get('createdDateTime_lte'),
                      format
                    )}`}
                  color={'default' as ColorType}
                  onDelete={() => {
                    handleDeleteChip('date');
                  }}
                />
              )}

            {renderChipFilterTest()}

            {searchParams.get('pointMultiplierEventCreationId_eq') !== null && (
              <Chip
                label={`${searchParams.get(
                  'pointMultiplierEventCreationId_eq'
                )}`}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('pointMultiplierEventCreationId_eq');
                }}
              />
            )}

            {searchParams.get('pointMultiplierEventTitle_contains') !==
              null && (
              <Chip
                label={`${searchParams.get(
                  'pointMultiplierEventTitle_contains'
                )}`}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('pointMultiplierEventTitle_contains');
                }}
              />
            )}

            {role === ROLE_SDC_ADMIN[USER_TYPE.ADMINISTRATOR] &&
              searchParams.get('merchantId_label') !== null && (
                <Chip
                  label={searchParams.get('merchantId_label')}
                  color={'default' as ColorType}
                  onDelete={() => {
                    handleDeleteChip('merchantId_eq');
                  }}
                />
              )}

            {searchParams.get('processId_label') !== null && (
              <Chip
                label={searchParams.get('processId_label')}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('processId_eq');
                }}
              />
            )}
            {searchParams.get('associatedPointId_eq') !== null && (
              <Chip
                label={searchParams.get('associatedPointId_eq') as string}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('associatedPointId_eq');
                }}
              />
            )}
            {searchParams.get('salesCode_contains') !== null && (
              <Chip
                label={searchParams.get('salesCode_contains') as string}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('salesCode_contains');
                }}
              />
            )}
            {searchParams.get('accountId_eq') !== null && (
              <Chip
                label={searchParams.get('accountId_eq')}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('accountId_eq');
                }}
              />
            )}
            {searchParams.get('id_eq') !== null && (
              <Chip
                label={searchParams.get('id_eq')}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('id_eq');
                }}
              />
            )}

            {!IsNullOrEmptyOrUndefined(
              searchParams.get('pointStatusEnum_eq')
            ) && (
              <Chip
                label={
                  searchParams.get('pointStatusEnum_eq') === '1'
                    ? 'Active'
                    : searchParams.get('pointStatusEnum_eq') === '2'
                    ? 'Expired'
                    : '--'
                }
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('pointStatusEnum_eq');
                }}
              />
            )}

            {!IsNullOrEmptyOrUndefined(
              searchParams.get('excessRedemptionAmount_filterBy')
            ) && (
              <Chip
                label={
                  transactionExcessRedemptionAmountFilterOptions.find(
                    (item: any) =>
                      item.value ===
                      searchParams.get('excessRedemptionAmount_filterBy')
                  )?.label ?? '--'
                }
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip('excessRedemptionAmount_filterBy');
                }}
              />
            )}

            {searchParams.get('transactionFilterType_by') &&
              valueFilter?.transactionFilterType_by?.map((item, index) => (
                <Chip
                  label={item.title}
                  color={'default' as ColorType}
                  onDelete={() => {
                    valueFilter?.transactionFilterType_by?.splice(index, 1);
                    setValueFilter({
                      ...valueFilter,
                      transactionFilterType_by:
                        valueFilter?.transactionFilterType_by,
                    });
                    setParams(preState => ({
                      ...preState,
                      _page: 0,
                      _size: DEFAULT_FILTER_PAGE_SIZE,
                      transactionFilterType_by:
                        valueFilter?.transactionFilterType_by.length > 0
                          ? valueFilter?.transactionFilterType_by
                              .map(item => item.value)
                              .toString()
                          : undefined,
                    }));
                  }}
                />
              ))}
          </StyledStack>
          <Tooltips title="Filter list">
            <IconButton
              aria-label="Filter list"
              size="large"
              onClick={toggleModal}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltips>
        </Grid>
      </Grid>
      <Divider my={6} />

      <Table
        dataSource={dataSource}
        columns={columns}
        page={params._page}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onOpenModalInfo={toggleOpenModalInfomations}
        rowsPerPage={params._size}
        textNodata="There are no all transaction(s) matching the filter."
        order={order}
        orderBy={orderBy}
        dense={dense}
        isMultiCheckbox={false}
        count={totalPage}
        isLoading={isLoading}
      >
        {!error && (
          <TableBody>
            {dataSource?.map((row, index) => {
              const tableRowProps = {
                hover: true,
                role: 'checkbox',
                key: index,
                tabIndex: -1,
                ['data-group-id']: getGroupId(row),
                onMouseEnter: () => onHoverRow(row),
                onMouseLeave: () => onBlurRow(row),
              };
              if (isAdministrator) {
                return (
                  <StyledTableRow {...tableRowProps}>
                    <TableCell align="left">{row.customerId}</TableCell>
                    <TableCell align="left">
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                        }}
                      >
                        {row.id}
                        {row.isTest === true && (
                          <Chip
                            label="Test"
                            color={'default' as ColorType}
                            sx={{
                              height: 20,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      {row.associatedPointId ?? '--'}
                    </TableCell>
                    <TableCell align="left">{row.salesCode ?? '--'}</TableCell>
                    <TableCell component="th" scope="row">
                      {formatLocalDateFromUTCTime(
                        row.createdDateTime,
                        formatDateTimes
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {renderTransactionType(row)}
                    </TableCell>
                    <TableCell align="left">{renderSpentAmount(row)}</TableCell>
                    <TableCell align="left">
                      {renderRedemptionAmount(row)}
                    </TableCell>
                    <TableCell align="left">
                      {renderExcessRedemptionAmount(row)}
                    </TableCell>
                    <TableCell align="left">
                      {renderReceiptImage(row?.receiptImageUrl ?? null)}
                    </TableCell>
                    <TableCell align="left">
                      {renderSystemAssuranceLevel(row)}
                    </TableCell>
                    <TableCell align="left">
                      {row?.pointMultiplierEventTitle ?? '--'}
                    </TableCell>
                    <TableCell align="left">{renderFieldPoints(row)}</TableCell>
                    <TableCell align="left">
                      {formatLocalDateFromUTCTime(
                        row.expiryDateTime,
                        formatDateTimes
                      ) || '--'}
                    </TableCell>
                    <TableCell align="left">{renderStatus(row)}</TableCell>
                    <TableCell align="left">
                      {renderPointsStatus(row)}
                    </TableCell>
                    <TableCell
                      align="right"
                      padding="checkbox"
                      style={{paddingRight: '10px'}}
                    >
                      {renderAction(row.id, row)}
                    </TableCell>
                  </StyledTableRow>
                );
              }
              return (
                <StyledTableRow {...tableRowProps}>
                  <TableCell align="left">{row.customerId}</TableCell>
                  <TableCell align="left">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      {row.id}
                      {row.isTest === true && (
                        <Chip
                          label="Test"
                          color={'default' as ColorType}
                          sx={{
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    {row.associatedPointId ?? '--'}
                  </TableCell>
                  <TableCell align="left">{row.salesCode ?? '--'}</TableCell>
                  <TableCell component="th" scope="row">
                    {formatLocalDateFromUTCTime(
                      row.createdDateTime,
                      formatDateTimes
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {renderTransactionType(row)}
                  </TableCell>
                  <TableCell align="left">{renderSpentAmount(row)}</TableCell>
                  <TableCell align="left">
                    {renderRedemptionAmount(row)}
                  </TableCell>
                  <TableCell align="left">
                    {renderExcessRedemptionAmount(row)}
                  </TableCell>
                  <TableCell align="left">
                    {renderReceiptImage(row?.receiptImageUrl ?? null)}
                  </TableCell>
                  <TableCell align="left">
                    {row?.pointMultiplierEventTitle ?? '--'}
                  </TableCell>
                  <TableCell align="left">{renderFieldPoints(row)}</TableCell>
                  <TableCell align="left">{renderStatus(row)}</TableCell>
                  <TableCell
                    align="right"
                    padding="checkbox"
                    style={{paddingRight: '10px'}}
                  >
                    {renderAction(row.id, row)}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    </React.Fragment>
  );
}

export default AllTransaction;
